/*
 * Celbari "Order now · Cash on delivery" button (blocks/accelerated-checkout.liquid).
 * Replaces Shopify's dynamic "Buy it now" button, so its large checkout script does not load.
 * 1. The Order now button adds the selected variant (size/colour + quantity) to the cart with
 *    /cart/add.js, then opens /checkout. Sold-out size or no size: a clear message, no checkout.
 * 2. The sticky bottom bar button says e.g. "Order now · COD" and does the same.
 * Shopify's pixel still sends "added to cart" (from /cart/add.js) and "checkout started".
 */
(() => {
  if (window.celbariOrderNow) return;
  window.celbariOrderNow = true;

  const BLOCK = '[data-celbari-order-now-block]';
  const STICKY_BUTTON = '.sticky-add-to-cart__button';
  const MESSAGES = {
    noSize: 'Please choose a size first.',
    soldOut: 'Sorry, this size is sold out. Please choose another size.',
    failed: 'Something went wrong. Please try again.',
    busy: 'Opening checkout…',
  };
  const root = window.Shopify?.routes?.root || '/';

  const mainBlock = () => document.querySelector(BLOCK);

  /** @param {Element} block @param {string} text */
  function showMessage(block, text) {
    const message = block.querySelector('[data-celbari-order-now-message]');
    if (!message) return;
    message.textContent = text;
    message.hidden = !text;
  }

  /** Busy state on the main button and the sticky button, so a second tap does nothing */
  function setBusy(block, busy) {
    const label = block.querySelector('[data-celbari-order-now-label]');
    const button = block.querySelector('[data-celbari-order-now-button]');
    if (label) {
      if (busy && !label.dataset.text) label.dataset.text = label.textContent;
      if (!busy && label.dataset.text) label.textContent = label.dataset.text;
      if (busy) label.textContent = MESSAGES.busy;
    }
    [button, ...document.querySelectorAll(`${STICKY_BUTTON}[data-celbari-order-now="true"]`)].forEach((el) => {
      if (!el) return;
      el.toggleAttribute('aria-busy', busy);
      el.classList.toggle('is-busy', busy);
    });
  }

  /** @param {Element} block */
  async function orderNow(block) {
    const form = block.closest('form');
    if (!form || block.querySelector('[data-celbari-order-now-button]')?.hasAttribute('aria-busy')) return;

    const variantId = form.elements.namedItem('id')?.value;
    const quantity = Math.max(1, parseInt(form.elements.namedItem('quantity')?.value, 10) || 1);
    if (!variantId) {
      showMessage(block, MESSAGES.noSize);
      return;
    }
    // Horizon disables Add to cart when the chosen size is sold out
    if (form.querySelector('[ref="addToCartButton"]')?.disabled) {
      showMessage(block, MESSAGES.soldOut);
      return;
    }

    showMessage(block, '');
    setBusy(block, true);
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        // Shopify's own text only when it is about the cart limit ("All 20 … are in your cart")
        const description = String(data.description || '');
        const stockText = /cart/i.test(description) ? description : MESSAGES.soldOut;
        showMessage(block, response.status === 422 ? stockText : MESSAGES.failed);
        setBusy(block, false);
        return;
      }
      window.location.href = `${root}checkout`;
    } catch {
      showMessage(block, MESSAGES.failed);
      setBusy(block, false);
    }
  }

  // Sticky bar: show the Order now text and use the same logic
  function updateStickyBar() {
    const block = mainBlock();
    const stickyLabel = block?.getAttribute('data-celbari-sticky-label');
    document.querySelectorAll(STICKY_BUTTON).forEach((stickyButton) => {
      const textSpan = stickyButton.querySelector('.add-to-cart-text__content > span:first-child');
      if (!textSpan) return;
      if (block && stickyLabel && !stickyButton.disabled) {
        if (textSpan.textContent.trim() !== stickyLabel) textSpan.textContent = stickyLabel;
        if (stickyButton.dataset.celbariOrderNow !== 'true') stickyButton.dataset.celbariOrderNow = 'true';
      } else if (stickyButton.hasAttribute('data-celbari-order-now')) {
        stickyButton.removeAttribute('data-celbari-order-now');
      }
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const button = target.closest('[data-celbari-order-now-button]');
      if (button) {
        event.preventDefault();
        const block = button.closest(BLOCK);
        if (block) orderNow(block);
        return;
      }

      const stickyButton = target.closest(`${STICKY_BUTTON}[data-celbari-order-now="true"]`);
      const block = mainBlock();
      if (!stickyButton || !block) return;
      event.preventDefault();
      event.stopImmediatePropagation(); // not Horizon's add-to-cart
      orderNow(block).then(() => {
        const message = block.querySelector('[data-celbari-order-now-message]');
        if (message && !message.hidden) block.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    },
    true
  );

  // Coming back from checkout with the browser's Back button: button ready again
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) document.querySelectorAll(BLOCK).forEach((block) => setBusy(block, false));
  });

  // Hide an old message once the shopper picks another size
  document.addEventListener('change', (event) => {
    const form = event.target instanceof Element ? event.target.closest('form') : null;
    const block = form?.querySelector(BLOCK) || (event.target.closest?.('variant-picker') && mainBlock());
    if (block) showMessage(block, '');
  });

  let scheduled = false;
  function refresh() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      updateStickyBar();
    });
  }

  // Only react to changes in the sticky bar (Horizon re-renders it when the size changes)
  const inScope = (node) => {
    const element = node.nodeType === 1 ? node : node.parentElement;
    return Boolean(element?.closest('sticky-add-to-cart'));
  };
  const addsScope = (node) =>
    node.nodeType === 1 && (node.matches('sticky-add-to-cart') || Boolean(node.querySelector('sticky-add-to-cart')));

  new MutationObserver((records) => {
    for (const record of records) {
      if (inScope(record.target)) return refresh();
      for (const node of record.addedNodes) {
        if (addsScope(node)) return refresh();
      }
    }
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['disabled', 'hidden'],
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();
})();
